/**
 * gift-crosssell.js — auto-adds companion items to the cart.
 *
 * Two features share this module:
 *
 * 1. FREE GIFT PER VARIANT (variant-cards block): a card's radio carries
 *    data-gift-variant-id. Whenever the cart changes, the cart is reconciled so
 *    that every "parent" variant in the cart has its gift line (tagged with the
 *    hidden line property `_gift_for: <parent variant id>`), with the gift
 *    quantity mirroring the parent quantity. Removing the parent removes the
 *    gift. The price of the gift is whatever the gift product/discount is
 *    configured to in admin (e.g. a 0€ gift product, or an automatic
 *    "Buy X get Y" 100% discount) — the theme only adds the line.
 *
 * 2. CROSS-SELL CHECKBOX (cross-sell-addon block): when the shopper adds the
 *    main product to the cart and the checkbox is ticked, the add-on variant is
 *    added once (skipped if it's already in the cart).
 *
 * Integration: listens for Horizon's `shopify:cart:lines-update` standard event
 * (dispatched by product-form / cart-items) and, after applying its own cart
 * mutations, dispatches the same event with a fresh /cart.js payload and no
 * `sections` HTML — cart-items-component then re-fetches its own section and
 * the cart icon updates its bubble, all through the theme's native code path.
 */
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const GIFT_PROP = '_gift_for';

const routes = (window.Theme && window.Theme.routes) || {};
// Theme.routes.cart_add_url already ends in .js (scripts.liquid appends it).
const ADD_URL = routes.cart_add_url || '/cart/add.js';
const CHANGE_URL = `${routes.cart_change_url || '/cart/change'}.js`;
const CART_URL = `${routes.cart_url || '/cart'}.js`;

const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

/** Variant ids whose add failed (e.g. sold out) — don't retry every event. */
const failedVariantIds = new Set();

/** True while we dispatch our own cart event, so our listener ignores it. */
let announcing = false;

/** Serializes syncs so two rapid cart events can't race each other. */
let chain = Promise.resolve();

/** `gid://shopify/ProductVariant/123` or `123` -> `123` */
function numericId(id) {
  const s = String(id);
  return s.includes('/') ? s.split('/').pop().split('?')[0] : s;
}

/** parent variant id -> gift variant id, read from the rendered variant cards. */
function giftMapFromDom() {
  const map = new Map();
  for (const input of document.querySelectorAll('.variant-cards__input[data-variant-id][data-gift-variant-id]')) {
    map.set(String(input.dataset.variantId), String(input.dataset.giftVariantId));
  }
  return map;
}

async function fetchCart() {
  const res = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

/**
 * Reconciles the cart: gift lines mirror their parent lines, orphaned gifts are
 * removed, and any requested cross-sell add-ons are added once.
 * @param {string[]} addonIds - cross-sell variant ids to add this pass.
 */
async function syncCart(addonIds) {
  const gifts = giftMapFromDom();
  if (gifts.size === 0 && addonIds.length === 0) return;

  const cart = await fetchCart();
  const items = cart.items || [];

  // Quantity of each non-gift variant in the cart.
  const parentQty = new Map();
  for (const line of items) {
    if (line.properties && line.properties[GIFT_PROP]) continue;
    const vid = String(line.variant_id);
    parentQty.set(vid, (parentQty.get(vid) || 0) + line.quantity);
  }

  const adds = [];
  const changes = [];

  for (const [parentId, giftId] of gifts) {
    const wanted = parentQty.get(parentId) || 0;
    const giftLine = items.find(
      (l) => l.properties && String(l.properties[GIFT_PROP]) === parentId && String(l.variant_id) === giftId
    );
    if (wanted > 0 && !giftLine && !failedVariantIds.has(giftId)) {
      adds.push({ id: Number(giftId), quantity: wanted, properties: { [GIFT_PROP]: parentId } });
    } else if (giftLine && wanted > 0 && giftLine.quantity !== wanted) {
      changes.push({ id: giftLine.key, quantity: wanted });
    }
  }

  // Gift lines whose parent left the cart (or whose configured gift changed).
  for (const line of items) {
    if (!line.properties || !line.properties[GIFT_PROP]) continue;
    const parentId = String(line.properties[GIFT_PROP]);
    const orphaned = !(parentQty.get(parentId) > 0);
    const remapped = gifts.has(parentId) && gifts.get(parentId) !== String(line.variant_id);
    if (orphaned || remapped) changes.push({ id: line.key, quantity: 0 });
  }

  for (const addonId of addonIds) {
    const alreadyInCart = items.some(
      (l) => String(l.variant_id) === String(addonId) && !(l.properties && l.properties[GIFT_PROP])
    );
    if (!alreadyInCart && !failedVariantIds.has(String(addonId))) {
      adds.push({ id: Number(addonId), quantity: 1 });
    }
  }

  if (adds.length === 0 && changes.length === 0) return;

  for (const change of changes) {
    await fetch(CHANGE_URL, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(change) });
  }
  const added = [];
  for (const item of adds) {
    // One request per item so a sold-out gift can't block the others.
    const res = await fetch(ADD_URL, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ items: [item] }) });
    if (res.ok) added.push(item);
    else failedVariantIds.add(String(item.id));
  }

  announce(await fetchCart(), added, changes);
}

/**
 * Tells the rest of the theme the cart changed. No `sections` HTML in the
 * payload → cart-items-component re-renders its section itself.
 */
function announce(cart, addedItems, changedLines) {
  // The standard-events payload validator wants a known action/context and
  // >= 1 line: describe the adds when there are any, else the qty changes.
  let action = 'add';
  let lines = addedItems.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity }));
  if (lines.length === 0) {
    action = changedLines.every((change) => change.quantity === 0) ? 'remove' : 'update';
    lines = changedLines.map((change) => ({ id: String(change.id), quantity: change.quantity }));
  }
  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({
    action,
    context: 'cart',
    lines,
    promise: deferred.promise,
  });
  announcing = true;
  document.dispatchEvent(event);
  announcing = false;
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { source: 'gift-crosssell', itemCount: cart.item_count, items: cart.items },
  });
}

function scheduleSync(addonIds) {
  chain = chain
    .then(() => syncCart(addonIds))
    .catch((error) => console.warn('[gift-crosssell]', error));
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (announcing) return;

  // Cross-sell: only when the trigger was an "add" of one of the parent
  // product's variants and the box is ticked.
  const addonIds = [];
  if (event.action === 'add' && Array.isArray(event.lines)) {
    const addedIds = new Set(event.lines.map((line) => numericId(line.merchandiseId)));
    for (const root of document.querySelectorAll('[data-cross-sell-addon]')) {
      const input = root.querySelector('.xsell__input');
      const addonId = root.dataset.addonVariantId;
      const parents = (root.dataset.parentVariantIds || '').split(',').filter(Boolean);
      if (!input || !input.checked || !addonId) continue;
      if (parents.length > 0 && !parents.some((id) => addedIds.has(id))) continue;
      addonIds.push(addonId);
    }
  }

  (event.promise || Promise.resolve(null))
    .then((result) => {
      if (result && result.detail && result.detail.didError) return;
      scheduleSync(addonIds);
    })
    .catch(() => {});
});

// Reconcile once on load too (self-heals a cart left in a stale state).
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scheduleSync([]));
} else {
  scheduleSync([]);
}
